import React from 'react';
import { Accordion, Code, Divider, Group, List, Modal, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAppContext } from '../contexts/AppContext';
import { BUURT_STATUSSEN, BUURT_SOORTEN } from './LegendaPaneel';

// Eén legenda-stip, zelfde vorm als op de kaart: vulling = status, rand = soort.
function Stip({ vulling, rand = '#fff' }) {
  vulling = vulling || '#5dade2';
  return (
    <span style={{
      display: 'inline-block', width: 13, height: 13, borderRadius: '50%',
      background: vulling, border: `2px solid ${rand}`, boxShadow: '0 0 0 1px #ddd',
      flexShrink: 0,
    }} />
  );
}

function Uitleg({ children }) {
  return <Text size="sm" c="dimmed">{children}</Text>;
}

// Helpvenster vanuit het gebruikersmenu. Bewust één plek waar staat hoe de
// applicatie werkt; de secties volgen de volgorde waarin je ze tegenkomt:
// navigeren -> lezen wat je ziet -> stations bewerken -> de rest per buurt.
export default function HelpPaneel({ open, onClose }) {
  const { user } = useAppContext();
  const smalScherm = useMediaQuery('(max-width: 768px)');
  const isBeheerder = !!user?.admin;

  return (
    <Modal
      opened={open} onClose={onClose} title="Uitleg" size="lg"
      fullScreen={smalScherm} transitionProps={{ duration: 0 }}
    >
      <Text size="sm" mb="sm">
        Netverbetering brengt de LS-buurtaanpak in beeld: van heel Nederland
        inzoomen tot één stationslocatie, met de bijbehorende afspraken,
        kabelgegevens en documenten.
      </Text>

      <Accordion variant="contained" chevronPosition="left" defaultValue="navigeren">
        <Accordion.Item value="navigeren">
          <Accordion.Control><Text size="sm" fw={600}>Navigeren op de kaart</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Uitleg>
                De kaart kent vijf niveaus: Nederland, regio, gemeente, buurt en
                station. Het kruimelpad bovenin laat zien waar je bent.
              </Uitleg>
              <List size="sm" spacing={4}>
                <List.Item><b>Inzoomen:</b> klik op een regio, gemeente, buurt of station.</List.Item>
                <List.Item><b>Terug:</b> korte <b>rechtermuisklik</b> op de kaart gaat één niveau omhoog. Houd je de rechterknop langer ingedrukt, dan kantel je de kaart.</List.Item>
                <List.Item><b>Direct springen:</b> gebruik de zoekbalk bovenin voor een station, buurt, gemeente of adres.</List.Item>
                <List.Item><b>Zijpaneel:</b> open of sluit het met de knop links of het pijltje aan de rand.</List.Item>
              </List>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="legenda">
          <Accordion.Control><Text size="sm" fw={600}>De stip lezen (legenda)</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Uitleg>
                Op buurtniveau vertelt elke stip twee dingen tegelijk. De
                <b> vulling</b> is de akkoordstatus van de locatie, de <b>rand</b> het
                soort werk. De legenda rechtsonder op de kaart toont hetzelfde.
              </Uitleg>

              <Text size="sm" fw={600} mt={4}>Vulling — akkoordstatus</Text>
              {BUURT_STATUSSEN.map(({ label, kleur }) => (
                <Group key={label} gap={8} wrap="nowrap">
                  <Stip vulling={kleur} />
                  <Text size="sm">{label}</Text>
                </Group>
              ))}
              <Uitleg>
                “Vervallen” betekent hier dat een <i>beoogd nieuw</i> station niet
                meer nodig is.
              </Uitleg>

              <Text size="sm" fw={600} mt={4}>Rand — soort werk</Text>
              {BUURT_SOORTEN.map(({ label, rand, vulling }) => (
                <Group key={label} gap={8} wrap="nowrap">
                  <Stip rand={rand} vulling={vulling} />
                  <Text size="sm">{label}</Text>
                </Group>
              ))}
              <Group gap={8} wrap="nowrap">
                <Stip vulling="#888" />
                <Text size="sm">Bestaand station</Text>
              </Group>
              <Uitleg>
                Nieuw, ombouw en vervangend volgen uit het veld <b>Type werk</b> in de
                stationszijbalk. <b>Vervangend station</b> is het nieuwe station;
                <b> te vervangen station</b> is het bestaande station dat daardoor
                verdwijnt — die twee koppel je aan elkaar (zie hieronder), waarna er een
                stippellijn tussen loopt. Een geselecteerd station krijgt tijdelijk een
                gele rand.
              </Uitleg>

              <Divider my={4} />
              <Uitleg>
                Op stationsniveau toont de legenda de <b>KLIC-kleuren</b>: welke kleur
                hoort bij welke kabel of leiding (laagspanning, gas, water, riool…).
                Alleen de soorten die in deze buurt voorkomen staan erin. Op
                gemeenteniveau toont de legenda het jaar van uitvoering; klik op een
                jaartal om erop te filteren.
              </Uitleg>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="stations">
          <Accordion.Control><Text size="sm" fw={600}>Stations plaatsen en verplaatsen</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Text size="sm" fw={600}>Op buurtniveau (knoppen links)</Text>
              <List size="sm" spacing={4}>
                <List.Item><b>Toevoegen</b> (groene +): knop aan, dan klikken op de kaart. Sneller: <Code>Ctrl</Code> + klik.</List.Item>
                <List.Item><b>Verwijderen</b> (rode −): knop aan, dan het station aanklikken.</List.Item>
                <List.Item><b>Verplaatsen</b> (oranje pijlen): knop aan en slepen. Sneller: houd <Code>Shift</Code> ingedrukt en sleep.</List.Item>
                <List.Item><b>Te vervangen station koppelen</b> (blauwe schakels): knop aan, klik eerst het vervangende station en daarna het bestaande station dat verdwijnt. Dat krijgt een rode rand, met een stippellijn ertussen. Eén vervangend station mag er meerdere vervangen; ontkoppelen doe je in de stationszijbalk.</List.Item>
              </List>

              <Text size="sm" fw={600} mt={4}>Op stationsniveau</Text>
              <List size="sm" spacing={4}>
                <List.Item>Sleep het stationshuisje direct naar de juiste plek — hier is geen knop of toets voor nodig.</List.Item>
                <List.Item><b>Draaien:</b> scroll met het muiswiel <i>terwijl je sleept</i>; het huisje draait dan in stappen van 2°.</List.Item>
                <List.Item>Bij <b>ombouw</b> en <b>te slopen</b> zie je een icoon in plaats van een huisje. Er wordt bewust geen plattegrond getekend, omdat die uit een kastmodel komt en niet klopt met het bestaande station. Slepen werkt gewoon.</List.Item>
              </List>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="locaties">
          <Accordion.Control><Text size="sm" fw={600}>Locatievarianten en akkoord</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Uitleg>
                Elk station kan meerdere locatievarianten hebben. Elke keer dat je een
                station verplaatst, ontstaat een nieuwe variant in de tijdlijn in de
                stationszijbalk.
              </Uitleg>
              <List size="sm" spacing={4}>
                <List.Item><b>Markeer als gekozen</b> (★): de variant waar het station komt te staan. De stip op buurtniveau staat op die locatie.</List.Item>
                <List.Item><b>Markeer voor akkoord</b>: klik door de status heen — geen status → Akkoord → Afgekeurd → Vervallen → geen status. Dit is de enige plek waar de status wordt bijgehouden en het is meteen de vulkleur van de stip.</List.Item>
                <List.Item>Meerdere locaties mogen tegelijk op Akkoord staan (de gemeente kan meerdere kandidaten goedkeuren). Welke het uiteindelijk wordt, geef je aan met “Markeer als gekozen”.</List.Item>
                <List.Item><b>Type werk</b>, stationstype en kleurstelling stel je in de stationszijbalk in. Type en kleur gelden per locatievariant.</List.Item>
              </List>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="buurt">
          <Accordion.Control><Text size="sm" fw={600}>Wat je per buurt kunt doen</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Uitleg>
                De buurtzijbalk bundelt alles per buurt in uitklapbare onderdelen.
              </Uitleg>
              <List size="sm" spacing={4}>
                <List.Item><b>Stations:</b> alle stations in de buurt, met een stip in de statuskleur. Klik erop om af te dalen.</List.Item>
                <List.Item><b>Belangentafels:</b> het overleg met de gemeente vastleggen. Een opname wordt uitgeschreven en per besproken locatie gekoppeld, waarna je de tekst nog kunt corrigeren.</List.Item>
                <List.Item><b>KLIC-data ophalen:</b> haalt kabels en leidingen op. Daarna zie je ze op stationsniveau, met de KLIC-kleuren in de legenda.</List.Item>
                <List.Item><b>Bewonersbrieven:</b> genereert de aankondigingsbrief (Word) met de gegevens van deze buurt, plus een KML met de stationslocaties voor Google Earth of My Maps.</List.Item>
                <List.Item><b>Rapportages:</b> genereert een concept-locatierapportage.</List.Item>
              </List>
              <Uitleg>
                Op stationsniveau kun je linksonder de overlay <b>Percelen met recht
                (MOOR)</b> aanzetten.
              </Uitleg>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {isBeheerder && (
          <Accordion.Item value="beheer">
            <Accordion.Control><Text size="sm" fw={600}>Beheer</Text></Accordion.Control>
            <Accordion.Panel>
              <List size="sm" spacing={4}>
                <List.Item><b>Gebruikersbeheer:</b> nieuwe aanmeldingen goedkeuren en gebruikers aan gemeenten of regio’s koppelen.</List.Item>
                <List.Item><b>Data-import:</b> een Excel met projectgegevens inlezen.</List.Item>
                <List.Item><b>Recente wijzigingen:</b> wie wat wanneer heeft aangepast.</List.Item>
                <List.Item><b>Changelog:</b> wat er per datum aan de applicatie is veranderd.</List.Item>
                <List.Item><b>Op- en aanmerkingen:</b> issues en wensen vastleggen en de status ervan bijhouden.</List.Item>
              </List>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        <Accordion.Item value="hulp">
          <Accordion.Control><Text size="sm" fw={600}>Iets gevonden dat niet klopt?</Text></Accordion.Control>
          <Accordion.Panel>
            <Uitleg>
              {isBeheerder
                ? 'Leg het vast onder Beheer › Op- en aanmerkingen, dan blijft het bewaard met een status. Vermeld het projectnummer of de buurt, dan is het makkelijk terug te vinden.'
                : 'Geef het door aan de beheerder, met het projectnummer of de buurt erbij. Onder Beheer wordt het als op- of aanmerking vastgelegd en opgevolgd.'}
            </Uitleg>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Modal>
  );
}
